import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { isThemeFlagOn } from "../../../pages/gameSetupStorage";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MAX_TITLE_CHARS = 250;
const MAX_THANKYOU_CHARS = 300;
const MIN_POINTS = 1;
const MAX_POINTS = 10;
const MIN_IMG_COUNT = 2;
const MAX_IMG_COUNT = 5;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const normalizeCountInput = (raw) => String(raw).replace(/\D/g, "");

/** Digits only, capped at `max` so the field can't hold a value over the limit. */
const clampCountInput = (raw, max) => {
  const digits = normalizeCountInput(raw);
  return digits && parseInt(digits, 10) > max ? String(max) : digits;
};

/** Parses a saved count, clamped to [min, max]; falls back to `min`. */
const clampCount = (raw, min, max) =>
  Math.min(max, Math.max(min, parseInt(raw, 10) || min));

/** Strips leading whitespace as the user types (matches the PHP title fields). */
const trimStart = (value) => value.replace(/^\s+/, "");

function parseTextList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

/** Text repeater shared by the rules and thank-you content lists. */
function TextRepeater({ items, placeholder, maxChars, min, max, addLabel, onChange, disabled }) {
  const update = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));

  const add = () => {
    if (items.length >= max) {
      Swal.fire("Error", `Keep ${max} at the most`, "error");
      return;
    }
    onChange([...items, ""]);
  };

  const remove = (index) => {
    if (items.length <= min) {
      Swal.fire("Error", `At least ${min} is required`, "error");
      return;
    }
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <>
      <div className="rules-repeater">
        {items.map((item, index) => {
          const charCount = String(item ?? "").length;
          return (
            <div className="rules-repeater-row" key={`item-${index}`}>
              <span className="rules-repeater-index">{index + 1}</span>
              <div className="rules-repeater-field">
                <textarea
                  className="rules-repeater-textarea"
                  placeholder={placeholder}
                  value={item}
                  onChange={(e) => update(index, e.target.value)}
                />
                <p className={`rules-char-count${charCount > maxChars ? " is-over" : ""}`}>
                  {charCount}/{maxChars}
                </p>
              </div>
              <button
                type="button"
                className="rules-remove-btn"
                onClick={() => remove(index)}
                disabled={items.length <= min || disabled}
                aria-label="Remove"
                title="Remove"
              >
                <i className="fa-solid fa-xmark" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="rules-add-btn"
        onClick={add}
        disabled={items.length >= max || disabled}
      >
        <i className="fa-solid fa-plus" aria-hidden /> {addLabel}
      </button>
    </>
  );
}

/** Numeric input that caps while typing and clamps to [min, max] on blur. */
function CountSetting({ id, label, value, setValue, min, max }) {
  return (
    <div className="rules-setting-row">
      <label htmlFor={id} className="rules-setting-label">
        {label}
        <span className="rules-setting-sublabel">
          Between {min} and {max}
        </span>
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="rules-setting-input"
        value={value}
        onChange={(e) => setValue(clampCountInput(e.target.value, max))}
        onBlur={() => setValue(String(clampCount(value, min, max)))}
      />
    </div>
  );
}

function ColorChip({ label, value, setValue }) {
  return (
    <label className="rules-color-chip">
      <span className="rules-color-chip-swatch" style={{ background: value }}>
        <input type="color" value={value} onChange={(e) => setValue(e.target.value)} />
      </span>
      <span className="rules-color-chip-label">{label}</span>
    </label>
  );
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [titleText, setTitleText] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [rules, setRules] = useState([""]);
  const [correctPoints, setCorrectPoints] = useState(String(MIN_POINTS));
  const [wrongPoints, setWrongPoints] = useState(String(MIN_POINTS));
  const [imgCount, setImgCount] = useState(String(MAX_IMG_COUNT));
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [buttonTextColor, setButtonTextColor] = useState("#000000");
  const [thankYouTextColor, setThankYouTextColor] = useState("#000000");
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    const loadedRules = parseTextList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    setTitleText(String(data.landing_page_title ?? "").slice(0, MAX_TITLE_CHARS));
    setThankYouText(
      String(data.custom_text_thank_you_page ?? "").trim().slice(0, MAX_THANKYOU_CHARS)
    );
    setCorrectPoints(String(clampCount(data.points, MIN_POINTS, MAX_POINTS)));
    setWrongPoints(String(clampCount(data.wrong_points, MIN_POINTS, MAX_POINTS)));
    setImgCount(String(clampCount(data.img_count ?? MAX_IMG_COUNT, MIN_IMG_COUNT, MAX_IMG_COUNT)));
    setTextColor(toColorInputValue(data.landing_page_title_color));
    setButtonColor(toColorInputValue(data.button_color || "#e9695e"));
    setButtonTextColor(toColorInputValue(data.button_Textcolor || "#000000"));
    setThankYouTextColor(toColorInputValue(data.ty_Textcolor || "#000000"));
    setLeaderboardEnabled(isThemeFlagOn(data.leaderboard, true));
  }, [data]);

  const validate = () => {
    const title = titleText.trim();
    if (!title) return { ok: false, msg: "Title cannot be empty." };
    if (title.length > MAX_TITLE_CHARS) {
      return { ok: false, msg: "You exceed the maximum limit of Title!" };
    }
    if (thankYouText.trim().length > MAX_THANKYOU_CHARS) {
      return {
        ok: false,
        msg: `Thank you page text must be below ${MAX_THANKYOU_CHARS} characters.`,
      };
    }

    const filledRules = rules.map((r) => String(r ?? "").trim()).filter(Boolean);
    if (!filledRules.length) return { ok: false, msg: "At least 1 rule is required." };
    if (rules.some((r) => String(r ?? "").length > MAX_RULE_CHARS)) {
      return { ok: false, msg: `Rules must be below ${MAX_RULE_CHARS} characters.` };
    }

    return {
      ok: true,
      payload: {
        landing_page_title: title,
        custom_text_thank_you_page: thankYouText.trim(),
        rules: filledRules,
        points: clampCount(correctPoints, MIN_POINTS, MAX_POINTS),
        wrong_points: clampCount(wrongPoints, MIN_POINTS, MAX_POINTS),
        img_count: clampCount(imgCount, MIN_IMG_COUNT, MAX_IMG_COUNT),
        landing_page_title_color: textColor,
        button_color: buttonColor,
        button_Textcolor: buttonTextColor,
        ty_Textcolor: thankYouTextColor,
        leaderboard: leaderboardEnabled ? "true" : "false",
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-align-left" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Rules &amp; Messaging</h2>
              <p className="rules-card-subtitle">What players read before, and after, the game</p>
            </div>
          </header>

          <div className="rules-card-body">
            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-title-text">
                Add Custom Title
              </label>
              <textarea
                id="rules-title-text"
                className="rules-textarea"
                maxLength={MAX_TITLE_CHARS}
                value={titleText}
                onChange={(e) => setTitleText(trimStart(e.target.value))}
              />
              <p className="rules-field-hint">
                <code>Maximum character limit should be {MAX_TITLE_CHARS} characters.</code>
                <code>This title will reflect on the main game page</code>
              </p>
            </div>

            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-thankyou-text">
                Custom Text Thank You Page
              </label>
              <textarea
                id="rules-thankyou-text"
                className="rules-textarea"
                maxLength={MAX_THANKYOU_CHARS}
                value={thankYouText}
                onChange={(e) => setThankYouText(trimStart(e.target.value))}
              />
              <p className="rules-field-hint">
                <code>This text will reflect on the thank you page.</code>
                <code>Maximum character limit should be {MAX_THANKYOU_CHARS} characters.</code>
              </p>
            </div>

            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Add Custom Rules</label>
              <TextRepeater
                items={rules}
                placeholder="Rules"
                maxChars={MAX_RULE_CHARS}
                min={MIN_RULES}
                max={MAX_RULES}
                addLabel="Add new rule"
                onChange={setRules}
                disabled={loading}
              />
              <p className="rules-field-hint">
                <code>Keep {MAX_RULES} rules at the most</code>
                <code>At least {MIN_RULES} rule is required</code>
                <code>Character limit of each rule is {MAX_RULE_CHARS}</code>
              </p>
            </div>
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-sliders" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Page Content</h2>
                <p className="rules-card-subtitle">Scoring and appearance</p>
              </div>
            </header>

            <div className="rules-card-body">
              <CountSetting
                id="rules-correct-points"
                label="Total Marks Per Right Answer"
                value={correctPoints}
                setValue={setCorrectPoints}
                min={MIN_POINTS}
                max={MAX_POINTS}
              />
              <CountSetting
                id="rules-wrong-points"
                label="Total Marks Per Wrong Answer"
                value={wrongPoints}
                setValue={setWrongPoints}
                min={MIN_POINTS}
                max={MAX_POINTS}
              />
              <CountSetting
                id="rules-img-count"
                label="Image Show Count"
                value={imgCount}
                setValue={setImgCount}
                min={MIN_IMG_COUNT}
                max={MAX_IMG_COUNT}
              />

              <div className="rules-setting-row">
                <label htmlFor="rules-leaderboard-toggle" className="rules-setting-label">
                  Show Leaderboard
                  <span className="rules-setting-sublabel">
                    {leaderboardEnabled
                      ? "Players see the ranked leaderboard after the game"
                      : "Players see only their score and the thank you message"}
                  </span>
                </label>
                <span className="rules-switch">
                  <input
                    id="rules-leaderboard-toggle"
                    type="checkbox"
                    role="switch"
                    checked={leaderboardEnabled}
                    onChange={(e) => setLeaderboardEnabled(e.target.checked)}
                  />
                  <span className="rules-switch-track" aria-hidden />
                </span>
              </div>

              <div className="rules-color-grid">
                <ColorChip label="Text Color" value={textColor} setValue={setTextColor} />
                <ColorChip label="Button Color" value={buttonColor} setValue={setButtonColor} />
                <ColorChip
                  label="Button Text Color"
                  value={buttonTextColor}
                  setValue={setButtonTextColor}
                />
                <ColorChip
                  label="Thankyou Content Text Color"
                  value={thankYouTextColor}
                  setValue={setThankYouTextColor}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions">
        <button
          type="button"
          className="save-and-continue"
          data-tour="tour-actions"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
